import { Linkedin } from 'lucide-react';
import { useParams } from 'react-router-dom';

import { PermissionsGate } from '@/modules/auth/components/permissions/PermissionsGate';
import { useCheckPermissions } from '@/modules/auth/hooks/useCheckPermissions';
import { permissions } from '@/modules/auth/permissions';
import { CandidateActionButton } from '@/modules/candidates/components/CandidateActionButton';
import { CandidateAssessmentPanel } from '@/modules/candidates/components/CandidateAssessmentPanel';
import { CandidateDetail } from '@/modules/candidates/components/CandidateDetail';
import { CandidateFeedbacksList } from '@/modules/candidates/components/CandidateFeedbacksList';
import { CandidateJobNotesList } from '@/modules/candidates/components/candidateJobNotes/CandidateJobNotesList';
import { HiringStatusDropdown } from '@/modules/candidates/components/HiringStatusDropdown';
import { useCandidateAssessmentQuery } from '@/modules/candidates/queries/useCandidateAssessmentQuery';
import { useCandidateJobQuery } from '@/modules/candidates/queries/useCandidateJobQuery';
import { useCompanyQuery } from '@/modules/companies/queries/useCompanyQuery';
import { useJobQuery } from '@/modules/jobs/queries/useJobQuery';
import { DocumentMetadata } from '@/modules/layouts/components/DocumentMetadata';
import { Alert, AlertDescription } from '@/modules/ui/components/alert/Alert';
import { Button } from '@/modules/ui/components/button/Button';
import Container from '@/modules/ui/components/container/Container';
import Loader from '@/modules/ui/components/loader/Loader';
import { PageHeader } from '@/modules/ui/components/page-header/PageHeader';

const JobCandidate = () => {
  const { jobId, candidateId } = useParams<{
    jobId: string;
    candidateId: string;
  }>();

  const candidateJobQuery = useCandidateJobQuery({
    jobId: jobId!,
    candidateId: candidateId!,
  });
  const hasUserPermission = useCheckPermissions();
  const canReadAssessment = hasUserPermission([
    permissions.CandidateAssessments.Read,
  ]);
  const assessmentQuery = useCandidateAssessmentQuery(
    { jobId: jobId!, candidateId: candidateId! },
    { enabled: Boolean(jobId && candidateId && canReadAssessment) },
  );

  const jobQuery = useJobQuery(jobId!);
  const companyId = jobQuery.data?.companyId;
  const companyQuery = useCompanyQuery(companyId || '', {
    enabled: !!companyId,
  });

  const fullName = `${candidateJobQuery.data?.candidate.firstName} ${candidateJobQuery.data?.candidate.lastName}`;

  const pageHeader = (
    <PageHeader
      title={fullName}
      action={
        <Button
          size="lg"
          onClick={() =>
            window.open(
              `https://www.linkedin.com/in/${candidateJobQuery.data?.candidate.linkedInProfileId}`,
              '_blank',
              'noopener,noreferrer',
            )
          }
          className="inline-flex items-center gap-2"
        >
          <Linkedin size={20} />
          <span>View LinkedIn profile</span>
        </Button>
      }
    />
  );

  return (
    <>
      <DocumentMetadata title={fullName} />

      {candidateJobQuery.isLoading && <Loader />}
      {candidateJobQuery.error && (
        <Container>
          {pageHeader}
          <Alert variant="destructive" className="mt-2">
            <AlertDescription>Failed to load candidate</AlertDescription>
          </Alert>
        </Container>
      )}

      {!candidateJobQuery.isLoading && candidateJobQuery.data && (
        <Container>
          {pageHeader}
          <div className="space-y-8">
            <CandidateDetail
              candidateJob={candidateJobQuery.data}
              additionalActions={
                <>
                  {candidateJobQuery.data.status === 'Shortlisted' && (
                    <PermissionsGate
                      permissions={[permissions.CandidateJobs.Write]}
                    >
                      <HiringStatusDropdown
                        jobId={candidateJobQuery.data.jobId}
                        candidateId={candidateJobQuery.data.candidate.id}
                        currentStatus={candidateJobQuery.data.hiringStatus}
                        hiringStages={
                          companyQuery.data?.hiringStages ?? undefined
                        }
                      />
                    </PermissionsGate>
                  )}
                  <CandidateActionButton
                    candidateJob={candidateJobQuery.data}
                    job={jobQuery.data}
                    displayedAssessmentRevisionId={
                      assessmentQuery.isSuccess
                        ? (assessmentQuery.data?.assessment.ledger
                            ?.displayedRevisionId ?? null)
                        : undefined
                    }
                  />
                </>
              }
            />
            <PermissionsGate
              permissions={[permissions.CandidateAssessments.Read]}
            >
              <CandidateAssessmentPanel
                jobId={candidateJobQuery.data.jobId}
                candidateId={candidateJobQuery.data.candidate.id}
              />
            </PermissionsGate>
            <CandidateFeedbacksList
              jobId={candidateJobQuery.data.jobId}
              candidateId={candidateJobQuery.data.candidate.id}
            />
            <CandidateJobNotesList candidateJobId={candidateJobQuery.data.id} />
          </div>
        </Container>
      )}
    </>
  );
};

export default JobCandidate;
